// features/customer/components/ShareAssetsModal.tsx
import React, { useState } from "react";
import { API_ROOT_URL } from "../../config/api";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

interface ShareableAsset {
  id: string;
  original_filename: string;
}

interface ShareAssetsModalProps {
  selectedAssets: ShareableAsset[];
  onClose: () => void;
  onShared: () => void;
}

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// Default expiry = 7 days from today, formatted for <input type="date">
const defaultExpiry = () => {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString().split("T")[0];
};

const ShareAssetsModal: React.FC<ShareAssetsModalProps> = ({
  selectedAssets,
  onClose,
  onShared,
}) => {
  const [guestEmail, setGuestEmail] = useState("");
  const [expiryDate, setExpiryDate] = useState(defaultExpiry());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const today = new Date().toISOString().split("T")[0];

  const handleSend = async () => {
    setError(null);

    if (!guestEmail.trim() || !isValidEmail(guestEmail.trim())) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!expiryDate || expiryDate < today) {
      setError("Please choose a valid future expiry date.");
      return;
    }
    if (selectedAssets.length === 0) {
      setError("No assets selected.");
      return;
    }

    setSubmitting(true);
    const toastId = toast.loading("Sending guest access...");
    try {
      const accessToken = localStorage.getItem("accessToken"); // adjust key to match your auth storage

      const res = await fetch(`${API_ROOT_URL}/assets/share`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({
          guestEmail: guestEmail.trim(),
          expiryDate,
          assetIds: selectedAssets.map((a) => a.id),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `Request failed with ${res.status}`);
      }
      toast.update(toastId, {
        render: "Guest access sent successfully!",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
      onShared();
    } catch (err: any) {
      const message = err?.message || "Failed to send guest access. Please try again.";
      setError(message);
      toast.update(toastId, {
        render: message,
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">Share Assets</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-gray-500">
            Sharing <strong>{selectedAssets.length}</strong> asset(s):
          </p>
          <ul className="max-h-24 overflow-y-auto text-xs text-gray-600 bg-gray-50 rounded-lg p-2 space-y-1">
            {selectedAssets.map((a) => (
              <li key={a.id} className="truncate">📄 {a.original_filename}</li>
            ))}
          </ul>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Guest Email
            </label>
            <input
              type="email"
              value={guestEmail}
              onChange={(e) => setGuestEmail(e.target.value)}
              placeholder="guest@example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Expiry Date
            </label>
            <input
              type="date"
              min={today}
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={submitting}
            className="px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 transition disabled:opacity-50"
          >
            {submitting ? "Sending..." : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareAssetsModal;